import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/common/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import DocumentPreviewModal from '../../components/ui/DocumentPreviewModal';
import { SystemInfoBox, SystemInformationSection } from '../../components/ui/SystemInformation';
import {
  CASE_SCENARIO_DESCRIPTION_MAX_LENGTH,
  CASE_SCENARIO_TITLE_MAX_LENGTH,
  createCaseScenario,
  getCaseScenarioById,
  updateCaseScenario,
  type CaseScenarioDetail,
  type CaseScenarioDocument,
} from '../../services/caseScenarioMasters';
import { uploadCaseFile } from '../../services/criminalCaseRequests';
import { setFlashToast } from '../../utils/flashToast';
import { formatDocumentTableDateTime } from '../../utils/dateFormat';
import { formatFileSizeLabel } from '../../utils/fileSize';
import {
  CASE_SCENARIO_PDF_ACCEPT,
  validateCaseScenarioPdf,
} from '../../utils/caseScenarioPdfUpload';
import {
  focusFirstInvalid,
  validateRequired,
  type RequiredErrors,
  type RequiredFieldSpec,
} from '../../utils/requiredValidation';
import { useAuth } from '../../contexts/AuthContext';
import { exceedsMax, maxCharLimitExceededError } from '../../utils/caseFieldValidation';
import { displayClientFileName } from '../../utils/fileDisplayName';

const FIELD_ERR_STYLE = { fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 } as const;

function FileDocIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function CloudUploadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

type UploadedPdfState = CaseScenarioDocument & {
  localFile: File | null;
};

function applyDetailToForm(
  d: CaseScenarioDetail,
  setters: {
    setTitle: (v: string) => void;
    setDescription: (v: string) => void;
    setActive: (v: boolean) => void;
    setCreatedAt: (v: string) => void;
    setCreatedBy: (v: string) => void;
    setUpdatedAt: (v: string) => void;
    setUpdatedBy: (v: string) => void;
    setPdf: (v: UploadedPdfState | null) => void;
  },
) {
  setters.setTitle(d.title);
  setters.setDescription(d.description);
  setters.setActive(d.active);
  setters.setCreatedAt(d.createdAt);
  setters.setCreatedBy(d.createdBy);
  setters.setUpdatedAt(d.updatedAt);
  setters.setUpdatedBy(d.updatedBy);
  setters.setPdf(
    d.document
      ? {
          ...d.document,
          localFile: null,
        }
      : null,
  );
}

export default function CaseScenarioFormPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { caseScenarioId } = useParams();
  const isEdit = Boolean(caseScenarioId);
  const nav = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const savingRef = useRef(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [reqErrors, setReqErrors] = useState<RequiredErrors>({});

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [pdf, setPdf] = useState<UploadedPdfState | null>(null);

  const [createdAt, setCreatedAt] = useState('');
  const [createdBy, setCreatedBy] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');
  const [updatedBy, setUpdatedBy] = useState('');

  const clearReqError = (key: string) => {
    setReqErrors((cur) => {
      if (!cur[key]) return cur;
      const next = { ...cur };
      delete next[key];
      return next;
    });
  };

  useEffect(() => {
    if (!isEdit || !caseScenarioId || !user) return;
    let alive = true;
    setLoading(true);
    void getCaseScenarioById(caseScenarioId).then((res) => {
      if (!alive) return;
      if (res.success) {
        applyDetailToForm(res.data, {
          setTitle,
          setDescription,
          setActive,
          setCreatedAt,
          setCreatedBy,
          setUpdatedAt,
          setUpdatedBy,
          setPdf,
        });
      } else {
        setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
      }
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [caseScenarioId, isEdit, t, user?.id]);

  const requiredSpecs = useMemo<RequiredFieldSpec[]>(() => {
    return [
      {
        key: 'title',
        label: t('metadata.caseScenarios.titleColumn'),
        value: title,
        elementId: 'case-scenario-title',
      },
      {
        key: 'pdf',
        label: t('metadata.caseScenarioForm.uploadPdf'),
        value: pdf?.fileName ?? '',
        elementId: 'case-scenario-pdf',
      },
    ];
  }, [pdf?.fileName, title, t]);

  const validationSpecs = useMemo<RequiredFieldSpec[]>(() => {
    return [
      ...requiredSpecs,
      {
        key: 'description',
        label: t('metadata.caseScenarios.description'),
        value: description,
        elementId: 'case-scenario-description',
      },
    ];
  }, [description, requiredSpecs, t]);

  const displayFileName = pdf
    ? displayClientFileName(pdf.originalFileName, pdf.fileName)
    : '';
  const displayFileSize = pdf ? formatFileSizeLabel(pdf.fileSize) : '';
  const fileBusy = uploading;

  async function handleFileSelected(file: File) {
    const validation = validateCaseScenarioPdf(file);
    if (!validation.ok) {
      setReqErrors((cur) => ({ ...cur, pdf: validation.message }));
      return;
    }

    setUploading(true);
    clearReqError('pdf');
    try {
      const res = await uploadCaseFile(file);
      if (!res.success) {
        setReqErrors((cur) => ({ ...cur, pdf: res.message ?? t('metadata.caseScenarioForm.uploadFailed') }));
        return;
      }
      setPdf({
        documentId: '',
        fileName: res.data.file_name,
        originalFileName: res.data.original_file_name ?? file.name,
        fileSize: res.data.file_size ?? file.size,
        localFile: file,
      });
    } finally {
      setUploading(false);
    }
  }

  function handleRemovePdf() {
    setRemoveConfirmOpen(false);
    setPdf(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setReqErrors((cur) => ({
      ...cur,
      pdf: t('metadata.caseScenarioForm.pdfRequired'),
    }));
  }

  async function handleSave() {
    if (savingRef.current) return;

    const errs = validateRequired(requiredSpecs);
    if (exceedsMax(title, CASE_SCENARIO_TITLE_MAX_LENGTH)) {
      errs.title = maxCharLimitExceededError();
    }
    if (exceedsMax(description, CASE_SCENARIO_DESCRIPTION_MAX_LENGTH)) {
      errs.description = maxCharLimitExceededError();
    }
    setReqErrors(errs);
    if (Object.keys(errs).length) {
      focusFirstInvalid(validationSpecs, errs);
      return;
    }

    savingRef.current = true;
    setSaving(true);
    let keepLocked = false;
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        active,
        uploadedFile:
          pdf?.localFile || !isEdit
            ? {
                fileName: pdf!.fileName,
                originalFileName: pdf!.originalFileName,
              }
            : undefined,
      };
      const res =
        isEdit && caseScenarioId
          ? await updateCaseScenario(caseScenarioId, payload)
          : await createCaseScenario(payload);
      if (!res.success) {
        setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
        return;
      }
      setFlashToast({
        message: isEdit
          ? t('metadata.caseScenarioForm.updateSuccess')
          : t('metadata.caseScenarioForm.createSuccess'),
        variant: 'success',
      });
      nav('/metadata/case-scenarios');
      keepLocked = true;
    } finally {
      if (!keepLocked) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  }

  return (
    <>
      <div className="bc">
        <span>{t('nav.groups.metadata')}</span>
        <span className="bc-sep">/</span>
        <Link to="/metadata/case-scenarios">{t('metadata.caseScenarios.title')}</Link>
        <span className="bc-sep">/</span>
        {isEdit ? t('metadata.caseScenarioForm.updateTitle') : t('metadata.caseScenarioForm.addTitle')}
      </div>

      <div className="pg-title">
        {isEdit ? t('metadata.caseScenarioForm.updateTitle') : t('metadata.caseScenarioForm.addTitle')}
      </div>

      <div className="cr-table-wrap" style={{ overflow: 'visible' }}>
        <div style={{ padding: 18 }}>
          {loading ? <div style={{ color: 'var(--t2)' }}>{t('common.loading')}</div> : null}
          {reqErrors.form ? <div className="si-err" style={{ marginBottom: 12 }}>{reqErrors.form}</div> : null}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="al-f" style={{ gridColumn: '1 / -1' }}>
              <div className="al-l">
                {t('metadata.caseScenarios.titleColumn')} <span className="req">*</span>
              </div>
              <input
                id="case-scenario-title"
                className="al-inp"
                value={title}
                maxLength={CASE_SCENARIO_TITLE_MAX_LENGTH}
                placeholder={t('metadata.caseScenarioForm.titlePlaceholder')}
                disabled={loading}
                style={reqErrors.title ? { borderColor: 'var(--red)' } : undefined}
                aria-invalid={reqErrors.title ? true : undefined}
                onChange={(e) => {
                  setTitle(e.target.value);
                  clearReqError('title');
                }}
              />
              {reqErrors.title ? <div style={FIELD_ERR_STYLE}>{reqErrors.title}</div> : null}
            </div>

            <div className="al-f" style={{ gridColumn: '1 / -1' }}>
              <div className="al-l">{t('metadata.caseScenarios.description')}</div>
              <textarea
                id="case-scenario-description"
                className="al-inp"
                style={{
                  height: 90,
                  paddingTop: 10,
                  paddingBottom: 10,
                  resize: 'vertical',
                  ...(reqErrors.description ? { borderColor: 'var(--red)' } : {}),
                }}
                value={description}
                maxLength={CASE_SCENARIO_DESCRIPTION_MAX_LENGTH}
                placeholder={t('metadata.caseScenarioForm.descriptionPlaceholder')}
                disabled={loading}
                aria-invalid={reqErrors.description ? true : undefined}
                onChange={(e) => {
                  setDescription(e.target.value);
                  clearReqError('description');
                }}
              />
              {reqErrors.description ? <div style={FIELD_ERR_STYLE}>{reqErrors.description}</div> : null}
            </div>

            <div className="al-f" style={{ gridColumn: '1 / -1' }} id="case-scenario-pdf">
              <div className="al-l">
                {t('metadata.caseScenarioForm.uploadPdf')} <span className="req">*</span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={CASE_SCENARIO_PDF_ACCEPT}
                style={{ display: 'none' }}
                disabled={loading || fileBusy}
                onChange={(e) => {
                  const file = e.currentTarget.files?.[0] ?? null;
                  e.currentTarget.value = '';
                  if (!file) return;
                  void handleFileSelected(file);
                }}
              />
              {pdf ? (
                <div className="ccr-doc-upload-file">
                  <div className="ccr-doc-upload-file-ico">
                    <FileDocIcon />
                  </div>
                  <div className="ccr-doc-upload-file-body">
                    <div className="ccr-doc-upload-file-name" title={displayFileName}>
                      {displayFileName}
                    </div>
                    {displayFileSize ? (
                      <div className="ccr-doc-upload-file-meta">{displayFileSize}</div>
                    ) : null}
                  </div>
                  <div className="ccr-doc-upload-file-acts">
                    <button
                      type="button"
                      className="act-b"
                      aria-label={t('metadata.caseScenarioForm.viewDocument', { name: displayFileName })}
                      data-tip={t('metadata.caseScenarioForm.view')}
                      disabled={loading || fileBusy}
                      onClick={() => setPreviewOpen(true)}
                    >
                      <EyeIcon />
                    </button>
                    <button
                      type="button"
                      className="act-b del"
                      aria-label={t('metadata.caseScenarioForm.removeDocument', { name: displayFileName })}
                      data-tip={t('metadata.caseScenarioForm.remove')}
                      disabled={loading || fileBusy}
                      onClick={() => setRemoveConfirmOpen(true)}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="ccr-doc-upload-btn"
                  disabled={loading || fileBusy}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <CloudUploadIcon />
                  {uploading ? t('metadata.caseScenarioForm.uploading') : t('metadata.caseScenarioForm.upload')}
                </button>
              )}
              {reqErrors.pdf ? <div style={FIELD_ERR_STYLE}>{reqErrors.pdf}</div> : null}
            </div>

            <div className="al-f" style={{ gridColumn: '1 / -1' }}>
              <label className="ccr-chk" style={{ marginBottom: 0 }}>
                <input
                  type="checkbox"
                  checked={active}
                  disabled={loading}
                  onChange={(e) => setActive(e.target.checked)}
                />
                <span>{t('common.active')}</span>
              </label>
            </div>
          </div>

          {isEdit ? (
            <SystemInformationSection title={t('profile.systemInformation')}>
              <SystemInfoBox
                label={t('profile.createdAt')}
                value={formatDocumentTableDateTime(createdAt) || createdAt}
              />
              <SystemInfoBox label={t('profile.createdBy')} value={createdBy} />
              <SystemInfoBox
                label={t('profile.lastUpdatedAt')}
                value={formatDocumentTableDateTime(updatedAt) || updatedAt}
              />
              <SystemInfoBox label={t('profile.lastUpdatedBy')} value={updatedBy} />
            </SystemInformationSection>
          ) : null}
        </div>

        <div className="tft" style={{ background: 'color-mix(in srgb, var(--bg) 60%, var(--surface))' }}>
          <div />
          <div className="pgr">
            <button className="mfb" type="button" disabled={saving} onClick={() => setCancelConfirmOpen(true)}>
              {t('common.cancel')}
            </button>
            <Button
              variant="primary"
              onClick={() => {
                void handleSave();
              }}
              disabled={saving || loading || fileBusy}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('admin.shared.cancelChanges')}
        message={t('metadata.caseScenarioForm.cancelConfirm')}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          setCancelConfirmOpen(false);
          nav('/metadata/case-scenarios');
        }}
      />

      <ConfirmDialog
        isOpen={removeConfirmOpen}
        title={t('metadata.caseScenarioForm.removePdfTitle')}
        message={t('metadata.caseScenarioForm.removePdfConfirm')}
        onClose={() => setRemoveConfirmOpen(false)}
        onConfirm={handleRemovePdf}
      />

      <DocumentPreviewModal
        isOpen={previewOpen}
        documentName={title || t('metadata.caseScenarios.title')}
        fileName={pdf?.fileName ?? ''}
        originalFileName={pdf?.originalFileName ?? ''}
        localFile={pdf?.localFile ?? null}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  );
}
